/* HFT path monitor — feed handler, distributor, clock, replica, watchdogs.
 *
 * The low-latency path's state in one panel. Everything here is MEASURED from
 * the running engine; nothing is projected, cached or interpolated.
 *
 * The panel's job is to make three specific confusions impossible:
 *
 *   * A disabled subsystem vs a healthy quiet one. Zeroes look identical for
 *     both. Anything switched off says "disabled" instead of showing counters.
 *   * A large duplicate count as a problem. It is the redundant line working.
 *     Lost packets are the problem, and they are coloured differently.
 *   * A latency figure as trustworthy when the clock is not. If PTP is not
 *     locked, the percentiles carry that warning next to them rather than
 *     standing alone looking authoritative.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { fmtLatencyNs } from '@/lib/format'
import { Empty, Meter, seriesColorAt } from '@/components/viz'
import type { HftTelemetry, LatencyReport, LatencyStage } from '@/types'

const POLL_MS = 5_000

export function HftMonitor() {
  const [hft, setHft] = useState<HftTelemetry | null>(null)
  const [latency, setLatency] = useState<LatencyReport | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    const poll = async () => {
      const [h, l] = await Promise.allSettled([api.hft(), api.latency()])
      if (cancelled) return
      if (h.status === 'fulfilled') setHft(h.value)
      else setHft({ ok: false, error: 'the API could not be reached' })
      if (l.status === 'fulfilled') setLatency(l.value)
      setLoading(false)
      timer = window.setTimeout(() => void poll(), POLL_MS)
    }
    void poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [])

  if (loading) return <Panel title="HFT path"><Empty>Loading telemetry…</Empty></Panel>

  if (!hft?.ok) {
    return (
      <Panel title="HFT path">
        <Empty>
          {hft?.note ?? hft?.error ??
            'The trading engine is not reachable, so no HFT telemetry is available.'}
        </Empty>
      </Panel>
    )
  }

  const feed = hft.feed_handler
  const clock = hft.time_sync
  const rep = hft.replica
  const health = hft.health
  const dist = hft.distributor
  const accel = hft.accelerator
  const certified = Boolean(clock?.certified ?? clock?.timestamps_certified)

  return (
    <Panel title="HFT path">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>

        {/* --- overall trading gate --- */}
        {health && health.trading_permitted === false && (
          <Banner tone="danger">
            {health.critical_failures} critical component
            {health.critical_failures === 1 ? '' : 's'} failed its watchdog.
            The engine has stopped trading on state it cannot vouch for.
          </Banner>
        )}

        {/* --- clock --- */}
        <Section title="Time synchronisation (PTP / IEEE 1588)">
          {clock?.enabled === false ? (
            <Disabled>
              PTP monitoring is not enabled on this engine. Timestamps are
              therefore <b>not certified</b> — latency figures below are measured
              against an undisciplined system clock.
            </Disabled>
          ) : (
            <>
              <Row label="State" value={clock?.state ?? '—'}
                   tone={certified ? 'ok' : 'warn'} />
              <Row label="Offset" value={fmtLatencyNs(clock?.offset_ns)}
                   tone={certified ? 'ok' : 'warn'} />
              <Row label="Path delay" value={fmtLatencyNs(clock?.path_delay_ns)} />
              <Row label="Source" value={clock?.source ?? '—'} />
              <Row label="Lock losses" value={String(clock?.lock_losses ?? 0)}
                   tone={(clock?.lock_losses ?? 0) > 0 ? 'warn' : undefined} />
              {!certified && (
                <Banner tone="warn">
                  Timestamps from this host are NOT certified. Latency figures and
                  regulatory timestamps measured against this clock cannot be
                  relied upon.
                </Banner>
              )}
            </>
          )}
        </Section>

        {/* --- measured latency --- */}
        {latency?.stages && latency.stages.length > 0 && (
          <Section title={`Measured latency${certified ? '' : ' (clock not certified)'}`}>
            <div className="scroll-x">
              <table className="qx">
                <thead>
                  <tr>
                    <th>Stage</th><th className="r">n</th>
                    <th className="r">p50</th><th className="r">p95</th>
                    <th className="r">p99</th><th className="r">p99.9</th>
                    <th className="r">max</th>
                  </tr>
                </thead>
                <tbody>
                  {latency.stages.map((s) => (
                    <tr key={s.stage}>
                      <td>{s.stage}</td>
                      <td className="num r">{s.n.toLocaleString('en-IN')}</td>
                      <td className="num r">{fmtLatencyNs(s.p50)}</td>
                      <td className="num r">{fmtLatencyNs(s.p95)}</td>
                      <td className="num r">{fmtLatencyNs(s.p99)}</td>
                      <td className="num r">{fmtLatencyNs(s.p999)}</td>
                      <td className="num r">{fmtLatencyNs(s.max)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* The percentile shape, drawn on a log scale.
                A linear axis is the wrong choice here and not a matter of
                taste: p50 is tens of nanoseconds and max is often tens of
                milliseconds — six orders of magnitude. On a linear axis every
                percentile below the max collapses onto the baseline and the
                chart shows one bar, which is precisely the part nobody needs
                to see. */}
            <LatencyProfile stages={latency.stages} />

            <Note>
              {latency.note ??
                'Measured on this host and build. Not a guarantee, and not a ' +
                'figure to quote for other hardware.'}
              {!certified && (
                <span style={{ color: 'var(--warn)' }}>
                  {' '}These were measured against a clock that is not certified.
                </span>
              )}
            </Note>
          </Section>
        )}

        {/* --- feed handler --- */}
        <Section title="Multicast feed handler">
          {!feed?.enabled ? (
            <Disabled>
              The multicast feed handler is not enabled. Market data is arriving
              through the configured venue adapter instead — the normal
              configuration outside co-location.
            </Disabled>
          ) : (
            <>
              <Row label="Venue" value={feed.venue ?? '—'} />
              <Row label="State" value={feed.state ?? '—'}
                   tone={feed.state === 'DEGRADED' ? 'warn' : 'ok'} />
              <Row label="Line A packets" value={num(feed.packets_a)} />
              <Row label="Line B packets" value={num(feed.packets_b)}
                   tone={feed.packets_b ? undefined : 'warn'} />
              {/* Duplicates are the redundant line DOING ITS JOB, coloured
                  neutral so nobody reads a big number as a fault. */}
              <Row label="Arbitrated duplicates" value={num(feed.duplicates_discarded)} />
              <Row label="Sequence gaps" value={num(feed.gaps_detected)}
                   tone={(feed.gaps_detected ?? 0) > 0 ? 'warn' : undefined} />
              <Row label="Packets LOST" value={num(feed.packets_lost)}
                   tone={(feed.packets_lost ?? 0) > 0 ? 'danger' : 'ok'} />
              <Row label="Gap state" value={feed.gap_state ?? '—'}
                   tone={feed.gap_state === 'FAILED' ? 'danger'
                       : feed.gap_state === 'NONE' ? 'ok' : 'warn'} />
              <Row label="Heartbeat timeouts" value={num(feed.heartbeat_timeouts)}
                   tone={(feed.heartbeat_timeouts ?? 0) > 0 ? 'warn' : undefined} />
              <Row label="Ring-full drops" value={num(feed.ring_full_drops)}
                   tone={(feed.ring_full_drops ?? 0) > 0 ? 'warn' : undefined} />
              {feed.redundancy_note && <Banner tone="warn">{feed.redundancy_note}</Banner>}
              <Note>
                A large duplicate count is healthy: it is the second line covering
                the first. Lost packets are the number that matters.
              </Note>
            </>
          )}
        </Section>

        {/* --- distributor --- */}
        {dist && (
          <Section title="Market-data distributor">
            <Row label="Ticks in" value={num(dist.ticks_in)} />
            <Row label="Fan-outs" value={num(dist.fanouts)} />
            <Row label="Shared bus" value={dist.shared_bus ? 'open' : 'closed'} />
            {hft.subscribers && hft.subscribers.length > 0 && (
              <div style={{ marginTop: 'var(--sp-2)' }}>
                {hft.subscribers.map((s) => (
                  <div key={s.name} style={{ marginBottom: 'var(--sp-2)' }}>
                    <Meter
                      label={`${s.name} (${s.class}) backlog`}
                      value={s.backlog}
                      max={s.capacity || 1}
                      format={(v) => v.toLocaleString('en-IN')}
                    />
                    {s.loss_pct > 0 && (
                      <div style={{ fontSize: 9, color: 'var(--danger)', marginTop: 2 }}>
                        Dropping {s.loss_pct.toFixed(3)}% — this consumer is making
                        decisions on partial data.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* --- replica --- */}
        <Section title="Replica and fault tolerance">
          {rep?.enabled === false ? (
            <Disabled>
              Replication is not enabled. This engine is the sole active and may
              always send.
            </Disabled>
          ) : (
            <>
              <Row label="Replica" value={rep?.id ?? '—'} />
              <Row label="Role" value={rep?.role ?? '—'}
                   tone={rep?.role === 'ACTIVE' ? 'ok'
                       : rep?.role === 'STANDBY' ? undefined : 'danger'} />
              <Row label="May send orders" value={rep?.may_send_orders ? 'yes' : 'NO'}
                   tone={rep?.may_send_orders ? 'ok' : 'danger'} />
              <Row label="Fencing token" value={String(rep?.fencing_token ?? '—')} />
              <Row label="Peer" value={`${rep?.peer_id || '—'}${rep?.peer_alive ? ' (alive)' : ' (silent)'}`}
                   tone={rep?.peer_alive ? 'ok' : 'warn'} />
              <Row label="State lag" value={`${rep?.state_lag ?? 0} ticks`}
                   tone={Math.abs(rep?.state_lag ?? 0) > 1000 ? 'warn' : undefined} />
              {rep?.warning && <Banner tone="danger">{rep.warning}</Banner>}
            </>
          )}
        </Section>

        {/* --- accelerator --- */}
        {accel && (
          <Section title="Acceleration">
            <Row label="Device" value={accel.device ?? '—'} />
            <Row label="Backend"
                 value={accel.hardware ? 'hardware' : 'software fallback'}
                 tone={accel.hardware ? 'ok' : undefined} />
            <Row label="Signals emitted" value={num(accel.signals)} />
            <Row label="Blocked by hardware pre-check" value={num(accel.risk_precheck_blocks)} />
            <Note>
              {accel.measured_wire_to_signal_ns
                ? `Measured wire-to-signal: ${fmtLatencyNs(accel.measured_wire_to_signal_ns)}.`
                : 'No wire-to-signal latency has been measured on this host. ' +
                  'The field stays empty rather than showing a datasheet figure.'}
            </Note>
          </Section>
        )}

        {/* --- watchdogs --- */}
        {hft.components && hft.components.length > 0 && (
          <Section title="Component watchdogs">
            <div className="scroll-x">
              <table className="qx">
                <thead>
                  <tr>
                    <th>Component</th><th>State</th>
                    <th className="r">Beats</th><th className="r">Missed</th>
                    <th className="r">Last beat</th><th className="r">Restarts</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {hft.components.map((c) => (
                    <tr key={c.name}>
                      <td>
                        {c.name}
                        {c.critical && (
                          <span style={{
                            marginLeft: 5, fontSize: 9, color: 'var(--warn)',
                            border: '1px solid var(--warn)', borderRadius: 2,
                            padding: '0 3px',
                          }}>
                            CRITICAL
                          </span>
                        )}
                      </td>
                      <td style={{ color: stateColor(c.state), fontWeight: 600 }}>
                        {c.state}
                      </td>
                      <td className="num r">{c.beats.toLocaleString('en-IN')}</td>
                      <td className="num r" style={{
                        color: c.missed > 0 ? 'var(--warn)' : undefined,
                      }}>
                        {c.missed}
                      </td>
                      <td className="num r">{c.since_beat_ms} ms</td>
                      <td className="num r">{c.restarts}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{c.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {health && (
              <Note>
                {health.recoveries_attempted ?? 0} auto-recovery attempt(s),{' '}
                {health.recoveries_succeeded ?? 0} succeeded. Recovery is bounded:
                past the budget a component is quarantined for an operator rather
                than restarted forever.
              </Note>
            )}
          </Section>
        )}
      </div>
    </Panel>
  )
}

const num = (v?: number) => (v ?? 0).toLocaleString('en-IN')

function stateColor(state: string): string {
  switch (state) {
    case 'HEALTHY': return 'var(--ok)'
    case 'DEGRADED': return 'var(--warn)'
    case 'FAILED': return 'var(--danger)'
    case 'QUARANTINED': return 'var(--danger)'
    default: return 'var(--text-muted)'
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
        marginBottom: 'var(--sp-2)', paddingBottom: 3,
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </div>
    </section>
  )
}

function Row({ label, value, tone }: {
  label: string
  value: string
  tone?: 'ok' | 'warn' | 'danger'
}) {
  const color = tone === 'ok' ? 'var(--ok)'
              : tone === 'warn' ? 'var(--warn)'
              : tone === 'danger' ? 'var(--danger)'
              : 'var(--text-primary)'
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontSize: 'var(--fs-xs)',
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="num" style={{ color }}>{value}</span>
    </div>
  )
}

function Disabled({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: 'var(--sp-2)', background: 'var(--bg-input)',
      border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 'var(--sp-2)', fontSize: 9, color: 'var(--text-muted)',
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

function Banner({ tone, children }: {
  tone: 'danger' | 'warn'
  children: React.ReactNode
}) {
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--warn)'
  const bg = tone === 'danger' ? 'var(--danger-bg)' : 'var(--warn-bg)'
  return (
    <div style={{
      marginTop: 'var(--sp-2)', padding: 'var(--sp-2)',
      background: bg, border: `1px solid ${color}`,
      borderRadius: 'var(--radius-sm)', color,
      fontSize: 'var(--fs-2xs)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

/** Latency percentiles per stage, on a shared logarithmic scale.
 *
 *  One axis, one unit, one scale — the stages are directly comparable, which is
 *  the whole point of putting them together. */
function LatencyProfile({ stages }: { stages: LatencyStage[] }) {
  const [hover, setHover] = useState<string | null>(null)

  const points: { key: string; label: string }[] = [
    { key: 'p50', label: 'p50' },
    { key: 'p95', label: 'p95' },
    { key: 'p99', label: 'p99' },
    { key: 'p999', label: 'p99.9' },
    { key: 'max', label: 'max' },
  ]

  const read = (s: LatencyStage, key: string): number | undefined =>
    (s as unknown as Record<string, number | undefined>)[key]

  const all = stages
    .flatMap((s) => points.map((p) => read(s, p.key)))
    .filter((v): v is number => v != null && Number.isFinite(v) && v > 0)
  if (all.length === 0) return null

  const lo = Math.log10(Math.min(...all))
  const hi = Math.log10(Math.max(...all))
  const span = hi - lo || 1
  const frac = (v: number) => (v > 0 ? (Math.log10(v) - lo) / span : 0)

  return (
    <div style={{ marginTop: 'var(--sp-2)' }}>
      {stages.map((s, si) => (
        <div key={s.stage} style={{ marginBottom: 'var(--sp-2)' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 9, color: 'var(--text-muted)', marginBottom: 2,
          }}>
            <span>{s.stage}</span>
            <span className="num">n={s.n.toLocaleString('en-IN')}</span>
          </div>
          <div style={{
            position: 'relative', height: 22,
            background: 'var(--bg-input)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
          }}>
            {points.map((p, pi) => {
              const v = read(s, p.key)
              if (v == null || !Number.isFinite(v) || v <= 0) return null
              const id = `${s.stage}:${p.key}`
              const left = frac(v) * 100
              return (
                <div
                  key={p.key}
                  onMouseEnter={() => setHover(id)}
                  onMouseLeave={() => setHover(null)}
                  title={`${s.stage} ${p.label}: ${fmtLatencyNs(v)}`}
                  style={{
                    position: 'absolute', top: 2, bottom: 2,
                    left: `calc(${left}% - 4px)`, width: 8,
                    background: seriesColorAt(pi),
                    // A 2px surface ring keeps overlapping marks readable when
                    // two percentiles land on nearly the same value.
                    boxShadow: '0 0 0 2px var(--bg-input)',
                    borderRadius: 2, cursor: 'pointer',
                    transform: hover === id ? 'scaleY(1.25)' : undefined,
                  }}
                />
              )
            })}
          </div>
          {si === stages.length - 1 && (
            <div style={{
              display: 'flex', gap: 'var(--sp-3)', marginTop: 4,
              fontSize: 9, color: 'var(--text-muted)', flexWrap: 'wrap',
            }}>
              {points.map((p, pi) => (
                <span key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{
                    width: 8, height: 8, background: seriesColorAt(pi),
                    borderRadius: 2, display: 'inline-block',
                  }} />
                  {p.label}
                </span>
              ))}
              <span style={{ marginLeft: 'auto' }}>logarithmic scale</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
